import Modal from "@/components/controls/Modal";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Dropdown from "@/components/controls/Dropdown/Dropdown";
import UserBreadcrumbs from "@/components/controls/UserBreadcrumbs/UserBreadcrumbs";
import { useWinRateBreadcrumbs } from "@/components/layouts/WinRate/WinRateBreadcrumbsContext";
import WinRateDrilldownContent from "./WinRateDrilldownContent";
import Text from "@/components/ui/text";
import { periodsDropdownProps } from "@/components/layouts/WinRate/helpers";
import {
  periodEnumToString,
  periodStringToEnum
} from "@/entities/reporting/helpers";
import { SettingsPeriod } from "@/entities/reporting/types";
import FeedbackButton from "@/components/controls/FeedbackButton";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  period: SettingsPeriod;
  periodSelect: (period: SettingsPeriod) => void;
}

const WinRateDrilldownModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  period,
  periodSelect
}) => {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const [minHeight, setMinHeight] = useState<number | undefined>(undefined);

  const { breadcrumbIds, setSelectedUserId, dynamicTableData, isLoading } =
    useWinRateBreadcrumbs();

  useEffect(() => {
    if (contentRef.current && !minHeight && !isLoading) {
      const height = contentRef.current.offsetHeight;
      if (height > 0) {
        setMinHeight(height);
      }
    }
  }, [dynamicTableData, isLoading, minHeight]);

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      contentClassname="w-[95%]"
      title={
        <div className="flex">
          <div className="flex flex-col gap-3 flex-1">
            <div className="flex flex-col flex-nowrap items-start justify-between align-start">
              <UserBreadcrumbs
                title={t("winRate.title")}
                subtitle={t("winRate.allDirectReports")}
                ids={breadcrumbIds}
                setter={setSelectedUserId}
              />
            </div>
          </div>
          <div className="flex-1 flex justify-end items-start">
            <FeedbackButton
              tooltip={t("feedback")}
              idContext="winrate-drilldown"
              variant="iconNoBg"
            />
          </div>
        </div>
      }
      content={
        <div
          ref={contentRef}
          className="flex flex-col h-full w-full py-4 pr-6"
          style={{ minHeight: minHeight ? `${minHeight}px` : "20rem" }}
        >
          <div className="w-full h-fit flex justify-between pl-5 pb-5">
            <Dropdown
              {...periodsDropdownProps(t)}
              defaultValue={periodEnumToString(period)}
              valueSelected={(value) => periodSelect(periodStringToEnum(value))}
            />
            <div className="flex gap-2 items-center">
              <Text
                as="span"
                variant="bodySecondary"
              >{`${t("winRate.calculate")}:`}</Text>
              <Dropdown
                options={{
                  toDate: t("winRate.methods.toDate")
                }}
                defaultValue={"toDate"}
                disabled
              />
            </div>
          </div>
          <WinRateDrilldownContent />
        </div>
      }
    />
  );
};

export default WinRateDrilldownModal;
